import {
    createApi,
    fetchBaseQuery
} from "@reduxjs/toolkit/query/react";

const API_URL = "/api";

export const interviewApi = createApi({
    reducerPath: "interviewApi",
    baseQuery: fetchBaseQuery({
        baseUrl: API_URL
    }),
    tagTypes: ["Interview", "InterviewAttempt"],
    endpoints: (builder) => ({
        // Schedule Interview
        scheduleInterview: builder.mutation({
            query: ({
                Token,
                formData
            }) => ({
                url: "/interview/schedule",
                method: "POST",
                headers: {
                    Authorization: `Bearer ${Token}`,
                },
                body: formData,
            }),
            invalidatesTags: ["Interview"],
        }),

        // Register for Interview (Student takes interview)
        registerInterview: builder.mutation({
            query: ({
                Token,
                formData,
                id
            }) => ({
                url: `/interview/${id}/take`,
                method: "POST",
                headers: {
                    Authorization: `Bearer ${Token}`,
                },
                body: formData,
            }),
            invalidatesTags: ["Interview"],
        }),

        // Get Interviews by Batch
        getInterviewsByBatch: builder.query({
            query: ({
                batchCode,
                Token
            }) => ({
                url: `/interview/batch/${batchCode}`,
                method: "GET",
                headers: {
                    Authorization: `Bearer ${Token}`,
                },
            }),
            providesTags: ["Interview"],
        }),

        // Get Student Interviews
        getStudentInterviews: builder.query({
            query: ({
                studentId,
                batchCode,
                Token
            }) => ({
                url: `/interview/student/${studentId}/batch/${batchCode}`,
                method: "GET",
                headers: {
                    Authorization: `Bearer ${Token}`,
                },
            }),
            providesTags: ["Interview"],
        }),

        // Get Single Interview
        getInterviewById: builder.query({
            query: ({
                id,
                Token
            }) => ({
                url: `/interview/${id}`,
                method: "GET",
                headers: {
                    Authorization: `Bearer ${Token}`,
                },
            }),
            providesTags: (result, error, {
                id
            }) => [{
                type: "Interview",
                id
            }],
            // Optional: more specific tagging
        }),

        // Student registers to take interview (create StudentInterview) - Mentor can also call this to 'agree' a student
        // store/instructor/interview.ts
        studentTakeInterview: builder.mutation({
            query: ({
                interviewId,
                Token,
                formData
            }) => ({
                url: `/interview/${interviewId}/take`,
                method: 'POST',
                body: formData,
                headers: {
                    Authorization: `Bearer ${Token}`
                },
            }),
            invalidatesTags: (result, error, {
                interviewId
            }) => [{
                    type: 'Interview',
                    id: interviewId
                },
                {
                    type: 'InterviewAttempt',
                    id: 'LIST'
                },
            ],
        }),

        // Get all student attempts for an interview (mentor view)
        getInterviewAttempts: builder.query({
            query: ({
                interviewId,
                Token
            }) => ({
                url: `/interview/${interviewId}/attempts`,
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${Token}`,
                },
            }),
            providesTags: (result, error, {
                interviewId
            }) => {
                const attempts = (result?.data?.result) || [];
                const attemptTags = Array.isArray(attempts) ?
                    attempts.map((attempt) => ({
                        type: 'InterviewAttempt',
                        id: attempt ?._id
                    })) : [];
                return [{
                        type: 'Interview',
                        id: interviewId
                    },
                    {
                        type: 'InterviewAttempt',
                        id: 'LIST'
                    },
                    ...attemptTags,
                ];
            },
        }),

        // Grade a student's interview attempt (mentor submits scores)
        gradeStudentInterview: builder.mutation({
            query: ({
                studentInterviewId,
                Token,
                formData
            }) => ({
                url: `/interview/grade/${studentInterviewId}`,
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${Token}`,
                    'Content-Type': 'application/json'
                },
                body: formData,
            }),
            invalidatesTags: (result, error, {
                studentInterviewId
            }) => [
                'Interview',
                {
                    type: 'InterviewAttempt',
                    id: studentInterviewId
                },
                {
                    type: 'InterviewAttempt',
                    id: 'LIST'
                },
            ],
        }),

        // Get the Grade of student interview attempt (mentor get scores list)
        getRanksStudentInterview: builder.query({
            query: ({
                batchCode,
                Token
            }) => ({
                url: `/interview/module-ranking/${batchCode}`,
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${Token}`,
                    'Content-Type': 'application/json'
                },
            }),
            providesTags: (result, error, {
                batchCode
            }) => [
                'Interview',
                {
                    type: 'InterviewAttempt',
                    id: 'LIST'
                },
                {
                    type: 'Interview',
                    id: batchCode
                },
            ],
        }),

        // Update Interview
        updateInterview: builder.mutation({
            query: ({
                id,
                Token,
                formData
            }) => ({
                url: `/interview/${id}`,
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${Token}`,
                },
                body: formData,
            }),
            invalidatesTags: ["Interview"],
        })  ,

        // Cancel Interview
        cancelInterview: builder.mutation({
            query: ({
                id,
                Token
            }) => ({
                url: `/interview/${id}`,
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${Token}`,
                },
            }),
            invalidatesTags: ["Interview"],
        }),


        // Legacy alias kept for backwards compatibility; prefer getInterviewAttempts
        useGetInterviewAttemptsQuery: builder.query({
            query: ({
                interviewId,
                Token
            }) => ({
                url: `/interview/${interviewId}/attempts`,
                headers: {
                    Authorization: `Bearer ${Token}`
                },
            }),
            providesTags: [{
                type: 'InterviewAttempt',
                id: 'LIST'
            }],
        }),
    }),
});

export const {
    useScheduleInterviewMutation,
    useRegisterInterviewMutation, // ← Don't forget this!
    useGetInterviewsByBatchQuery,
    useStudentTakeInterviewMutation,
    useGetInterviewAttemptsQuery,
    useGradeStudentInterviewMutation,
    useGetStudentInterviewsQuery,
    useGetInterviewByIdQuery,
    useUpdateInterviewMutation,
    useCancelInterviewMutation,
} = interviewApi;